/* eslint-disable no-bitwise -- this module's whole job is bit-packed grid
   storage and index math; bitwise ops here are the intent, not a slip. */
import { distanceMeters, type LngLat } from '../../maps/mapboxDrivingRoute';

/**
 * Grid-based indoor routing over a real walkable mask baked from Mapbox's
 * Indoor v3 tileset (see scripts/generate-yyz-nav-grid.mjs for how the mask
 * is produced and why a grid rather than a polygon-vertex graph).
 *
 * The guarantee this provides, which the previous vertex-graph could not:
 * a route can only ever step between ADJACENT walkable cells, and a cell is
 * walkable only if its centre is inside real walkable floor and outside
 * every obstacle (walls, the `inaccessible` class, carousels, shops). So a
 * path physically cannot cross a wall or cut through furniture — not as a
 * tuning heuristic, but structurally.
 *
 * Routes also prefer the middle of open space rather than scraping along
 * walls, because A* here is weighted by each cell's clearance (its distance
 * to the nearest obstacle). That yields the same practical result as
 * computing a medial axis / straight skeleton and routing along it, without
 * implementing skeletonization.
 */

export type NavGridData = {
  origin: [number, number];
  cellSizeMeters: number;
  cellLng: number;
  cellLat: number;
  cols: number;
  rows: number;
  walkableBase64: string;
};

export type NavGrid = {
  origin: [number, number];
  cellLng: number;
  cellLat: number;
  cellSizeMeters: number;
  cols: number;
  rows: number;
  /** Bit-packed walkability, row-major from `origin` (SW corner), LSB-first. */
  bits: Uint8Array;
  /** Distance (in cells) from each cell to the nearest non-walkable cell. */
  clearance: Float32Array;
};

const B64_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

/** Dependency-free base64 → bytes. Deliberately not relying on `atob` or
 * `Buffer`: neither is guaranteed present across React Native runtimes, and
 * this decoder runs once at module load. */
function decodeBase64(input: string): Uint8Array {
  const lookup = new Uint8Array(256);
  for (let i = 0; i < B64_CHARS.length; i++) lookup[B64_CHARS.charCodeAt(i)] = i;

  let validLength = input.length;
  while (validLength > 0 && input[validLength - 1] === '=') validLength--;
  const byteLength = Math.floor((validLength * 3) / 4);
  const bytes = new Uint8Array(byteLength);

  let byteIndex = 0;
  for (let i = 0; i < validLength; i += 4) {
    const c0 = lookup[input.charCodeAt(i)];
    const c1 = lookup[input.charCodeAt(i + 1)];
    const c2 = lookup[input.charCodeAt(i + 2)];
    const c3 = lookup[input.charCodeAt(i + 3)];
    if (byteIndex < byteLength) bytes[byteIndex++] = (c0 << 2) | (c1 >> 4);
    if (byteIndex < byteLength) bytes[byteIndex++] = ((c1 & 15) << 4) | (c2 >> 2);
    if (byteIndex < byteLength) bytes[byteIndex++] = ((c2 & 3) << 6) | c3;
  }
  return bytes;
}

/**
 * Chamfer distance transform (two passes, 3-4 weights, scaled back to
 * cells) — gives every walkable cell its distance to the nearest blocked
 * cell. Cheap enough to do at load time rather than shipping a second,
 * much larger array alongside the walkability mask.
 */
function computeClearance(bits: Uint8Array, cols: number, rows: number): Float32Array {
  const size = cols * rows;
  const dist = new Float32Array(size);
  const isSet = (i: number) => (bits[i >> 3] & (1 << (i & 7))) !== 0;

  const DIAGONAL = 4;
  const ORTHOGONAL = 3;
  const BIG = 1e9;

  for (let i = 0; i < size; i++) dist[i] = isSet(i) ? BIG : 0;

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      if (dist[i] === 0) continue;
      let best = dist[i];
      if (r > 0) best = Math.min(best, dist[i - cols] + ORTHOGONAL);
      if (c > 0) best = Math.min(best, dist[i - 1] + ORTHOGONAL);
      if (r > 0 && c > 0) best = Math.min(best, dist[i - cols - 1] + DIAGONAL);
      if (r > 0 && c < cols - 1) best = Math.min(best, dist[i - cols + 1] + DIAGONAL);
      dist[i] = best;
    }
  }
  for (let r = rows - 1; r >= 0; r--) {
    for (let c = cols - 1; c >= 0; c--) {
      const i = r * cols + c;
      if (dist[i] === 0) continue;
      let best = dist[i];
      if (r < rows - 1) best = Math.min(best, dist[i + cols] + ORTHOGONAL);
      if (c < cols - 1) best = Math.min(best, dist[i + 1] + ORTHOGONAL);
      if (r < rows - 1 && c < cols - 1) best = Math.min(best, dist[i + cols + 1] + DIAGONAL);
      if (r < rows - 1 && c > 0) best = Math.min(best, dist[i + cols - 1] + DIAGONAL);
      dist[i] = best;
    }
  }

  for (let i = 0; i < size; i++) dist[i] = dist[i] / ORTHOGONAL;
  return dist;
}

export function loadNavGrid(data: NavGridData): NavGrid {
  const bits = decodeBase64(data.walkableBase64);
  return {
    origin: data.origin,
    cellLng: data.cellLng,
    cellLat: data.cellLat,
    cellSizeMeters: data.cellSizeMeters,
    cols: data.cols,
    rows: data.rows,
    bits,
    clearance: computeClearance(bits, data.cols, data.rows),
  };
}

export function isWalkable(grid: NavGrid, col: number, row: number): boolean {
  if (col < 0 || col >= grid.cols || row < 0 || row >= grid.rows) return false;
  const i = row * grid.cols + col;
  return (grid.bits[i >> 3] & (1 << (i & 7))) !== 0;
}

export function cellToLngLat(grid: NavGrid, col: number, row: number): LngLat {
  return [
    grid.origin[0] + (col + 0.5) * grid.cellLng,
    grid.origin[1] + (row + 0.5) * grid.cellLat,
  ];
}

export function lngLatToCell(grid: NavGrid, point: LngLat): { col: number; row: number } {
  return {
    col: Math.floor((point[0] - grid.origin[0]) / grid.cellLng),
    row: Math.floor((point[1] - grid.origin[1]) / grid.cellLat),
  };
}

/**
 * Nearest walkable cell to `point`, searched outward in rings. Real inputs
 * routinely aren't on walkable floor — a label's centroid can land on a
 * carousel (an obstacle), a GPS fix drifts into a wall, a curbside pickup
 * point is outside the building entirely — so snapping is the normal case,
 * not an error path. Returns null only if nothing walkable is within
 * `maxRadiusCells`.
 */
export function nearestWalkableCell(
  grid: NavGrid,
  point: LngLat,
  maxRadiusCells = 120,
): { col: number; row: number } | null {
  const { col, row } = lngLatToCell(grid, point);
  if (isWalkable(grid, col, row)) return { col, row };

  for (let radius = 1; radius <= maxRadiusCells; radius++) {
    let best: { col: number; row: number } | null = null;
    let bestDist = Infinity;
    for (let dr = -radius; dr <= radius; dr++) {
      for (let dc = -radius; dc <= radius; dc++) {
        // Perimeter of the ring only — interior was covered by smaller radii.
        if (Math.max(Math.abs(dr), Math.abs(dc)) !== radius) continue;
        const c = col + dc;
        const r = row + dr;
        if (!isWalkable(grid, c, r)) continue;
        const d = dr * dr + dc * dc;
        if (d < bestDist) {
          bestDist = d;
          best = { col: c, row: r };
        }
      }
    }
    if (best) return best;
  }
  return null;
}

/** Binary min-heap keyed by f-score; plain arrays for speed on big grids. */
class MinHeap {
  private items: number[] = [];
  private priorities: number[] = [];

  get size(): number {
    return this.items.length;
  }

  push(item: number, priority: number): void {
    this.items.push(item);
    this.priorities.push(priority);
    let i = this.items.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.priorities[parent] <= this.priorities[i]) break;
      this.swap(i, parent);
      i = parent;
    }
  }

  pop(): number | undefined {
    if (this.items.length === 0) return undefined;
    const top = this.items[0];
    const lastItem = this.items.pop()!;
    const lastPriority = this.priorities.pop()!;
    if (this.items.length > 0) {
      this.items[0] = lastItem;
      this.priorities[0] = lastPriority;
      let i = 0;
      for (;;) {
        const left = 2 * i + 1;
        const right = left + 1;
        let smallest = i;
        if (left < this.items.length && this.priorities[left] < this.priorities[smallest]) smallest = left;
        if (right < this.items.length && this.priorities[right] < this.priorities[smallest]) smallest = right;
        if (smallest === i) break;
        this.swap(i, smallest);
        i = smallest;
      }
    }
    return top;
  }

  private swap(a: number, b: number): void {
    const item = this.items[a];
    this.items[a] = this.items[b];
    this.items[b] = item;
    const priority = this.priorities[a];
    this.priorities[a] = this.priorities[b];
    this.priorities[b] = priority;
  }
}

/** Clearance (in cells) at or above which a cell is considered comfortably
 * mid-corridor and gets no penalty. ~4m from the nearest wall. */
const COMFORTABLE_CLEARANCE_CELLS = 4;
/** How strongly to avoid scraping along walls. At 1.6, hugging a wall costs
 * ~2.6x more than walking down the middle, so A* takes a wall-hugging
 * shortcut only when the detour would otherwise be substantially longer —
 * which is also how a person actually behaves. */
const CLEARANCE_WEIGHT = 1.6;

function stepCost(grid: NavGrid, index: number, diagonal: boolean): number {
  const clearance = grid.clearance[index];
  const shortfall = Math.max(0, COMFORTABLE_CLEARANCE_CELLS - clearance) / COMFORTABLE_CLEARANCE_CELLS;
  const base = diagonal ? Math.SQRT2 : 1;
  return base * (1 + CLEARANCE_WEIGHT * shortfall);
}

/**
 * A* over walkable cells, 8-connected. Diagonal steps additionally require
 * both shared orthogonal neighbours to be open, so a path can never squeeze
 * through the corner point between two walls — a classic grid-routing bug
 * that visually reads as clipping through a wall corner.
 */
export function findGridPath(
  grid: NavGrid,
  start: { col: number; row: number },
  goal: { col: number; row: number },
): Array<{ col: number; row: number }> | null {
  const { cols, rows } = grid;
  const size = cols * rows;
  const startIndex = start.row * cols + start.col;
  const goalIndex = goal.row * cols + goal.col;
  if (!isWalkable(grid, start.col, start.row) || !isWalkable(grid, goal.col, goal.row)) return null;
  if (startIndex === goalIndex) return [start];

  const gScore = new Float32Array(size).fill(Infinity);
  const cameFrom = new Int32Array(size).fill(-1);
  const closed = new Uint8Array(size);
  const open = new MinHeap();

  const heuristic = (index: number) => {
    const c = index % cols;
    const r = (index / cols) | 0;
    const dc = Math.abs(c - goal.col);
    const dr = Math.abs(r - goal.row);
    // Octile distance — admissible for 8-connected movement.
    return Math.max(dc, dr) + (Math.SQRT2 - 1) * Math.min(dc, dr);
  };

  gScore[startIndex] = 0;
  open.push(startIndex, heuristic(startIndex));

  while (open.size > 0) {
    const current = open.pop()!;
    if (current === goalIndex) {
      const path: Array<{ col: number; row: number }> = [];
      let node = current;
      while (node !== -1) {
        path.push({ col: node % cols, row: (node / cols) | 0 });
        node = cameFrom[node];
      }
      return path.reverse();
    }
    if (closed[current]) continue;
    closed[current] = 1;

    const cc = current % cols;
    const cr = (current / cols) | 0;

    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (dc === 0 && dr === 0) continue;
        const nc = cc + dc;
        const nr = cr + dr;
        if (!isWalkable(grid, nc, nr)) continue;
        const diagonal = dc !== 0 && dr !== 0;
        if (diagonal && (!isWalkable(grid, cc + dc, cr) || !isWalkable(grid, cc, cr + dr))) continue;
        const neighbor = nr * cols + nc;
        if (closed[neighbor]) continue;
        const tentative = gScore[current] + stepCost(grid, neighbor, diagonal);
        if (tentative < gScore[neighbor]) {
          gScore[neighbor] = tentative;
          cameFrom[neighbor] = current;
          open.push(neighbor, tentative + heuristic(neighbor));
        }
      }
    }
  }
  return null;
}

/** Whether every cell along the straight segment a→b is walkable. Sampled
 * at half-cell steps, which is dense enough that no blocked cell between
 * two open ones can be stepped over. */
export function segmentIsWalkable(
  grid: NavGrid,
  a: { col: number; row: number },
  b: { col: number; row: number },
): boolean {
  const dc = b.col - a.col;
  const dr = b.row - a.row;
  const steps = Math.ceil(Math.max(Math.abs(dc), Math.abs(dr)) * 2);
  if (steps === 0) return isWalkable(grid, a.col, a.row);
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const c = Math.round(a.col + dc * t);
    const r = Math.round(a.row + dr * t);
    if (!isWalkable(grid, c, r)) return false;
  }
  return true;
}

/**
 * Collapses a jagged cell-by-cell path into the fewest straight segments
 * that still stay walkable ("string pulling"). Without this, an 8-connected
 * grid path is a staircase of 1m steps, which both looks wrong and makes
 * turn-by-turn guidance fire constantly on meaningless 45° zigzags.
 */
export function simplifyPath(
  grid: NavGrid,
  path: Array<{ col: number; row: number }>,
): Array<{ col: number; row: number }> {
  if (path.length <= 2) return path;
  const result = [path[0]];
  let anchor = 0;
  while (anchor < path.length - 1) {
    let farthest = anchor + 1;
    for (let candidate = path.length - 1; candidate > anchor; candidate--) {
      if (segmentIsWalkable(grid, path[anchor], path[candidate])) {
        farthest = candidate;
        break;
      }
    }
    result.push(path[farthest]);
    anchor = farthest;
  }
  return result;
}

/**
 * Full indoor route between two real-world points on one floor, as a
 * simplified LngLat polyline. Both endpoints are snapped to real walkable
 * floor first (see nearestWalkableCell). Returns null if either endpoint
 * has no walkable floor anywhere near it, or the two are genuinely
 * unreachable from each other.
 */
export function routeOnFloor(grid: NavGrid, from: LngLat, to: LngLat): LngLat[] | null {
  const start = nearestWalkableCell(grid, from);
  const goal = nearestWalkableCell(grid, to);
  if (!start || !goal) return null;
  const path = findGridPath(grid, start, goal);
  if (!path) return null;
  return simplifyPath(grid, path).map(cell => cellToLngLat(grid, cell.col, cell.row));
}

/** Total length of a polyline, metres. */
export function polylineLengthMeters(points: LngLat[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += distanceMeters(points[i - 1], points[i]);
  return total;
}
